import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import { studentsAPI, usersAPI, routesAPI } from '../../services/api';
import PaginatedTable from '../../components/PaginatedTable';

function StudentsManager() {
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', parent_id: '', route_id: '', home_address: '', latitude: '', longitude: '', google_maps_link: '' });
  const [editingId, setEditingId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const { t } = useI18n();
  const fileInputRef = useRef(null);

  const [locationsOpen, setLocationsOpen] = useState(false);
  const [locationsStudent, setLocationsStudent] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locForm, setLocForm] = useState({ label: '', address: '', latitude: '', longitude: '', is_primary: false, google_maps_link: '' });

  const extractCoords = (link, target = 'form') => {
    if (!link) return;
    const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
    const match = link.match(regex);
    if (match) {
      if (target === 'form') {
        setForm(prev => ({ ...prev, latitude: match[1], longitude: match[2] }));
      } else if (target === 'locForm') {
        setLocForm(prev => ({ ...prev, latitude: match[1], longitude: match[2] }));
      }
    }
  };

  const loadData = async () => {
    try {
      const [s, u, r] = await Promise.all([studentsAPI.list(), usersAPI.list(), routesAPI.list()]);
      setStudents(s.data);
      setParents(u.data.filter((u) => u.role === 'parent'));
      setRoutes(r.data);
    } catch (err) { setError('Failed to load data.'); }
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const fd = new FormData();
      fd.append('first_name', form.first_name);
      fd.append('last_name', form.last_name);
      fd.append('parent_id', form.parent_id);
      fd.append('route_id', form.route_id || '');
      fd.append('home_address', form.home_address);
      fd.append('latitude', parseFloat(form.latitude) || 0);
      fd.append('longitude', parseFloat(form.longitude) || 0);
      if (photoFile instanceof File) {
        fd.append('photo', photoFile);
      }

      if (editingId) {
        await studentsAPI.update(editingId, fd);
      } else {
        await studentsAPI.create(fd);
      }
      setForm({ first_name: '', last_name: '', parent_id: '', route_id: '', home_address: '', latitude: '', longitude: '' });
      setEditingId(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed.');
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({
      first_name: s.first_name, last_name: s.last_name,
      parent_id: s.parent_id, route_id: s.route_id || '',
      home_address: s.home_address,
      latitude: s.home_location?.y || '',
      longitude: s.home_location?.x || ''
    });
    setPhotoFile(null);
    setPhotoPreview(s.photo_url || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Soft delete this student?')) return;
    try { await studentsAPI.remove(id); loadData(); } catch (err) { setError('Delete failed.'); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openLocations = async (student) => {
    setLocationsStudent(student);
    setLocationsOpen(true);
    try {
      const res = await studentsAPI.listLocations(student.id);
      setLocations(res.data);
    } catch (err) { setError('Failed to load locations.'); }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!locForm.label || !locForm.latitude || !locForm.longitude) return;
    try {
      await studentsAPI.addLocation(locationsStudent.id, {
        label: locForm.label,
        address: locForm.address,
        latitude: parseFloat(locForm.latitude),
        longitude: parseFloat(locForm.longitude),
        is_primary: locForm.is_primary
      });
      setLocForm({ label: '', address: '', latitude: '', longitude: '', is_primary: false });
      const res = await studentsAPI.listLocations(locationsStudent.id);
      setLocations(res.data);
    } catch (err) { setError('Failed to add location.'); }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      await studentsAPI.removeLocation(locationsStudent.id, locationId);
      const res = await studentsAPI.listLocations(locationsStudent.id);
      setLocations(res.data);
    } catch (err) { setError('Failed to delete location.'); }
  };

  const columns = [
    { 
      key: 'photo_url', 
      label: t('photo'), 
      render: (v) => {
        if (!v) return <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#999' }}>?</div>;
        
        // Build the base URL: if REACT_APP_BACKEND_URL is not set, use window.location.origin
        // Standard relative path for proxied environments
        let finalPath = v;
        if (v.startsWith('/uploads/')) {
          finalPath = '/api' + v;
        }
        
        // Use relative URL so the browser/proxy handles the domain and port
        const fullUrl = finalPath;
        
        console.log('Rendering image with relative URL:', fullUrl);
        return <img src={fullUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} onError={(e) => console.error('Image failed to load:', fullUrl)} />;
      }
    },
    { key: 'first_name', label: t('firstName') },
    { key: 'last_name', label: t('lastName') },
    { key: 'parent_name', label: t('parent'), render: (v) => v || '-' },
    { key: 'route_name', label: t('route'), render: (v) => v || <span style={{ color: '#999' }}>{t('unassigned')}</span> },
    { key: 'home_address', label: t('address') },
  ];

  const locColumns = [
    { key: 'label', label: t('name') },
    { key: 'address', label: t('address'), render: (v) => v || '-' },
    { key: 'is_primary', label: t('primary'), render: (v) => v ? <span style={{ color: '#28a745', fontWeight: 'bold' }}>★</span> : '-' },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <Link to="/admin" style={{ color: '#007bff', textDecoration: 'none' }}>{t('backToDashboard')}</Link>
      <h1>{t('manageStudents')}</h1>

      {error && <div style={{ background: '#fee', color: '#c00', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{error}</div>}

      <form onSubmit={handleSubmit} style={styles.form} encType="multipart/form-data">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
          <div style={{ position: 'relative' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eee', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #ccc' }}>
              {photoPreview ? <img src={photoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#999', fontSize: 24 }}>📷</span>}
            </div>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoChange} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileInputRef.current.click()} style={{ position: 'absolute', bottom: 0, right: 0, background: '#007bff', color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 12 }}>+</button>
          </div>
          {photoPreview && <button type="button" onClick={handleRemovePhoto} style={{ fontSize: 12, color: '#c00', background: 'none', border: 'none', cursor: 'pointer' }}>{t('removePhoto')}</button>}
        </div>
        <input placeholder={t('firstNamePlaceholder')} value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required style={styles.input} />
        <input placeholder={t('lastNamePlaceholder')} value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required style={styles.input} />
        <select value={form.parent_id} onChange={(e) => setForm({...form, parent_id: e.target.value})} required style={styles.input}>
          <option value="">{t('selectParent')}</option>
          {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={form.route_id} onChange={(e) => setForm({...form, route_id: e.target.value})} style={styles.input}>
          <option value="">{t('noRoute')}</option>
          {routes.map((r) => <option key={r.id} value={r.id}>{r.route_name}</option>)}
        </select>
        <input placeholder={t('homeAddressPlaceholder')} value={form.home_address} onChange={(e) => setForm({...form, home_address: e.target.value})} required style={styles.input} />
        <input 
          placeholder={t('Google Maps Link')} 
          value={form.google_maps_link} 
          onChange={(e) => {
            setForm({...form, google_maps_link: e.target.value});
            extractCoords(e.target.value, 'form');
          }} 
          style={styles.input} 
        />
        <input placeholder={t('latitudePlaceholder')} type="number" step="any" value={form.latitude} onChange={(e) => setForm({...form, latitude: e.target.value})} style={styles.input} />
        <input placeholder={t('longitudePlaceholder')} type="number" step="any" value={form.longitude} onChange={(e) => setForm({...form, longitude: e.target.value})} style={styles.input} />
        <button type="submit" style={styles.saveBtn}>{editingId ? t('update') : t('create')}</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ first_name: '', last_name: '', parent_id: '', route_id: '', home_address: '', latitude: '', longitude: '' }); setPhotoFile(null); setPhotoPreview(null); }} style={styles.cancelBtn}>{t('cancel')}</button>}
      </form>

      <PaginatedTable
        data={students}
        columns={columns}
        searchFields={['first_name', 'last_name', 'parent_name', 'route_name', 'home_address']}
        onEdit={handleEdit}
        onDelete={handleDelete}
        extraActions={(row) => (
          <button onClick={() => openLocations(row)} style={{ background: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
            {t('manageLocations')}
          </button>
        )}
      />

      {locationsOpen && (
        <div style={styles.modalOverlay} onClick={() => setLocationsOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ margin: 0 }}>{locationsStudent?.first_name} {locationsStudent?.last_name} — {t('homeLocations')}</h2>
              <button onClick={() => setLocationsOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>{t('close')}</button>
            </div>

            <form onSubmit={handleAddLocation} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
              <input placeholder={t('locationLabel')} value={locForm.label} onChange={(e) => setLocForm({...locForm, label: e.target.value})} required style={styles.input} />
              <input placeholder={t('locationAddress')} value={locForm.address} onChange={(e) => setLocForm({...locForm, address: e.target.value})} style={styles.input} />
              <input 
                placeholder={t('Google Maps Link')} 
                value={locForm.google_maps_link} 
                onChange={(e) => {
                  setLocForm({...locForm, google_maps_link: e.target.value});
                  extractCoords(e.target.value, 'locForm');
                }} 
                style={styles.input} 
              />
              <input placeholder={t('latitudePlaceholder')} type="number" step="any" value={locForm.latitude} onChange={(e) => setLocForm({...locForm, latitude: e.target.value})} required style={{ ...styles.input, minWidth: 100 }} />
              <input placeholder={t('longitudePlaceholder')} type="number" step="any" value={locForm.longitude} onChange={(e) => setLocForm({...locForm, longitude: e.target.value})} required style={{ ...styles.input, minWidth: 100 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                <input type="checkbox" checked={locForm.is_primary} onChange={(e) => setLocForm({...locForm, is_primary: e.target.checked})} />
                {t('primary')}
              </label>
              <button type="submit" style={styles.saveBtn}>{t('addLocation')}</button>
            </form>

            {locations.length === 0 ? (
              <p style={{ color: '#999' }}>{t('noLocations')}</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {locColumns.map((col) => <th key={col.key} style={styles.th}>{col.label}</th>)}
                    <th style={styles.th}>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((loc) => (
                    <tr key={loc.id}>
                      {locColumns.map((col) => <td key={col.key} style={styles.td}>{col.render ? col.render(loc[col.key]) : loc[col.key]}</td>)}
                      <td style={styles.td}>
                        <button onClick={() => handleDeleteLocation(loc.id)} style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: '3px', padding: '3px 8px', cursor: 'pointer', fontSize: '12px' }}>{t('delete')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  form: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' },
  input: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '140px' },
  saveBtn: { padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  cancelBtn: { padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '8px', padding: '20px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' },
  th: { textAlign: 'left', padding: '8px', borderBottom: '2px solid #ddd', fontSize: '13px', color: '#555' },
  td: { padding: '8px', borderBottom: '1px solid #eee', fontSize: '13px' },
};

export default StudentsManager;
